'use client'
import { DefaultNodeTypes, SerializedLinkNode, type DefaultTypedEditorState} from '@payloadcms/richtext-lexical'
import { JSXConvertersFunction, LinkJSXConverter, RichText as ConvertRichText} from '@payloadcms/richtext-lexical/react'
import styles from './CodeBlock.module.scss'
import PullQuote from '../../containers/Blog/BlogEntry/BlogArticle/PullQuote'
import s from './rich-text.module.scss'

type NodeTypes = DefaultNodeTypes | {
  type: 'relationship';
  relationTo: string;
  value: any;
} | {
  type: 'block';
  blockType?: string;
  fields?: any;
}

const internalDocToHref = ({ linkNode }: { linkNode: SerializedLinkNode }) => {
    const { value, relationTo } = linkNode.fields.doc!
    if (typeof value !== 'object') {
      throw new Error('Expected value to be an object')
    }
    const slug = value.slug
    return relationTo === 'posts' ? `/posts/${slug}` : `/${slug}`
  }

export const createJsxConverters = (className?: string): JSXConvertersFunction<NodeTypes> => ({ defaultConverters }) => ({
  ...defaultConverters,
  ...LinkJSXConverter({ internalDocToHref }),
  block: ({ node }) => {
    if (node.fields?.blockType === 'codeBlock') {
      const language = node.fields.language || 'plaintext';
      const code = node.fields.code || '';
      
      // Use simple pre/code structure with theme classes
      return (
        <div className={styles.codeBlockContainer}>
          <div className={styles.codeBlockHeader}>
            <span className={styles.codeBlockLanguage}>
              {language.charAt(0).toUpperCase() + language.slice(1)}
            </span>
          </div>
          <pre className={styles.codeBlock}>
            <code className={`language-${language}`}>
              {code}
            </code>
          </pre>
        </div>
      );
    }
    
    return null;
  },
  relationship: ({ node }) => {
         // Handle pull quote relationships
     if (node.relationTo === 'pullQuotes' && node.value) {
       const pullQuoteData = node.value;
       
       return (
         <PullQuote 
           quote={pullQuoteData.quoteText || ''} 
           author={pullQuoteData.author || null} 
         />
       );
     }

    if (node.relationTo === 'markDownContent' && node.value?.markDownContent?.root) {
      const markdownData = node.value.markDownContent;
      
      // Check if this contains code blocks that need special handling
      const hasCodeBlocks = markdownData.root.children?.some((child: any) => 
        child.type === 'block' && child.fields?.blockType === 'codeBlock'
      );
      
      if (hasCodeBlocks) {
        // Manually render the content including code blocks
        return (
          <div className="markdown-content-wrapper">
            {markdownData.root.children.map((child: any, index: number) => {
              if (child.type === 'block' && child.fields?.blockType === 'codeBlock') {
                const language = child.fields.language || 'plaintext';
                const code = child.fields.code || '';
                
                return (
                  <div key={index} className={styles.codeBlockContainer}>
                    <div className={styles.codeBlockHeader}>
                      <span className={styles.codeBlockLanguage}>
                        {language.charAt(0).toUpperCase() + language.slice(1)}
                      </span>
                    </div>
                    <pre className={styles.codeBlock}>
                      <code className={`language-${language}`}>
                        {code}
                      </code>
                    </pre>
                  </div>
                );
              }
              
              // For non-code-block content, try to render with ConvertRichText
              return (
                <div key={index}>
                  <ConvertRichText
                    data={{ root: { ...markdownData.root, children: [child] } }}
                    converters={createJsxConverters(className)}
                    disableContainer={true}
                  />
                </div>
              );
            })}
          </div>
        );
      }
      
      // Create a new converters function for the nested content to ensure proper block handling
      const nestedConverters = createJsxConverters(className);
      
      // Recursively render the markdown content using the same RichText component
      return (
        <div className="markdown-content-wrapper">
          <ConvertRichText
            data={node.value.markDownContent}
            converters={nestedConverters}
            disableContainer={true}
          />
        </div>
      );
    }
    
    // Fallback for other relationship types
    return null;
  },
  autolink: ({ node, nodesToJSX }) => {
    const children = nodesToJSX({
      nodes: node.children,
    })

    const rel: string | undefined = node.fields.newTab ? 'noopener noreferrer' : undefined
    const target: string | undefined = node.fields.newTab ? '_blank' : undefined

    return (
      <a href={node.fields.url} {...{ rel, target }} className=''>
        {children}
      </a>
    )
  },
  link: ({ node, nodesToJSX }) => {
    const children = nodesToJSX({
      nodes: node.children,
    })

    const rel: string | undefined = node.fields.newTab ? 'noopener noreferrer' : undefined
    const target: string | undefined = node.fields.newTab ? '_blank' : undefined

    let href: string = node.fields.url ?? ''
    if (node.fields.linkType === 'internal') {
      if (internalDocToHref) {
        href = internalDocToHref({ linkNode: node })
      } else {
        console.error(
          'Lexical => JSX converter: Link converter: found internal link, but internalDocToHref is not provided',
        )
        href = '#' // fallback
      }
    }

    return (
      <a href={href} {...{ rel, target }}>
        {children}
      </a>
    )
  },
  list: ({ node, nodesToJSX }) => {
    const children = nodesToJSX({
      nodes: node.children,
    })
    const NodeTag = node.tag
    const listType = node.listType === 'number' ? 'decimal' : node.listType
    return <NodeTag className={`list-${listType} list-inside`}>{children}</NodeTag>
  },
  listitem: ({ node, nodesToJSX, parent }) => {
    const hasSubLists = node.children.some((child) => child.type === 'list')

    const children = nodesToJSX({
      nodes: node.children,
    })

    return (
      <li
        className={`${hasSubLists ? 'nestedListItem' : ''}`}
        style={hasSubLists ? { listStyleType: 'none' } : undefined}
        value={node?.value}
      >
        {children}
      </li>
    )
  },
  paragraph: ({ node, nodesToJSX }) => {
    const children = nodesToJSX({
      nodes: node.children,
    })
    
    return <p className={className}>{children}</p>
  },
  heading: ({ node, nodesToJSX }) => {
    const children = nodesToJSX({
      nodes: node.children,
    })
    const HeadingTag = node.tag || 'h2'
    return <HeadingTag className={className}>{children}</HeadingTag>
  },
})

interface Props {
  data: DefaultTypedEditorState
  enableGutter?: boolean
  enableProse?: boolean
  className?: string
  containerWidth?: boolean
}

const RichText: React.FC<Props> = (props) => {
  const { enableProse = true, enableGutter = true, className, containerWidth, ...rest } = props
  const containerWidthClass = containerWidth ? s.containerFull : s.containerDefault;

  return (
    <div className={`${containerWidthClass}`}>
      <ConvertRichText
        converters={createJsxConverters(className)}
        disableContainer={true}
        {...rest}
      />
    </div>
  )
}

export default RichText
  